/**
 * Stale-bundle self-heal (F1/F2 — cached-bundle compatibility across a deploy).
 *
 * After a deploy, a browser tab that loaded an OLDER SPA bundle keeps running
 * against the NEW API + asset set — `index.html` is `no-cache`, but `/assets/**`
 * is `immutable`, so an open tab never re-fetches its JS. Two symptoms follow:
 *
 *  1. A GraphQL operation the old bundle sends references a field the new schema
 *     removed or renamed (e.g. `interactionMap` → `siteGraph`,
 *     `renamePersonaProfile` → `updatePersonaProfileMeta` across the 2026-07
 *     deploy). The server rejects the whole document at validation with
 *     `GRAPHQL_VALIDATION_FAILED`, so the affected page silently renders empty.
 *  2. A lazy route/chunk the old bundle imports was replaced by a new content
 *     hash and now 404s → the dynamic import rejects (Vite fires
 *     `vite:preloadError`), and the launcher/route just fails.
 *
 * Both mean the same thing: "your bundle is older than the deploy." The fix is
 * the same: reload ONCE to pull the fresh `index.html` + bundle. The reload is
 * guarded by a `sessionStorage` timestamp so a *persistent* error (a genuine
 * bug, not staleness) can never send the tab into a reload loop — and if
 * `sessionStorage` is unavailable we decline to reload at all, choosing
 * loop-safety over self-heal.
 */

const RELOAD_STAMP_KEY = 'sx:stale-bundle-reload';

/**
 * Minimum gap between two auto-reloads. Long enough that a bundle which keeps
 * erroring after one reload can never loop; short enough that a *second*,
 * genuinely-new deploy landing later in the same session still self-heals.
 */
export const STALE_RELOAD_COOLDOWN_MS = 60_000;

// In-tick coalescing guard: a single stale document typically errors across
// several concurrent operations, each hitting the error link in the same tick.
// The first schedules the reload; the rest must be no-ops before the reload
// takes effect. Reset per page load (a reload gives a fresh module) and by
// `resetStaleBundleGuardForTests`.
let reloadScheduled = false;

/**
 * True when a GraphQL error is the signature of a stale bundle — a validation
 * failure, or a message naming a field/argument/type the current schema no
 * longer defines. Pure + exported for testing. It must stay conservative:
 * ordinary business/permission errors must return `false` so we never reload a
 * working page.
 */
export function isStaleBundleGraphQLError(err: {
  extensions?: { code?: unknown } | null;
  message?: string;
}): boolean {
  if (err.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') return true;
  const message = err.message ?? '';
  return /Cannot query field |Unknown argument |Unknown type |unknown field |is not defined by/i.test(
    message,
  );
}

/**
 * Decide whether to reload given the current time and the last-reload stamp.
 * Pure + exported for testing.
 */
export function shouldReloadForStaleBundle(now: number, lastReloadAt: number | null): boolean {
  if (lastReloadAt === null) return true;
  return now - lastReloadAt >= STALE_RELOAD_COOLDOWN_MS;
}

interface ReloadableWindow {
  location: Pick<Location, 'reload'>;
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
}

function readStamp(win: ReloadableWindow): number | null | 'unavailable' {
  try {
    const raw = win.sessionStorage.getItem(RELOAD_STAMP_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return 'unavailable';
  }
}

/**
 * Reload the page ONCE to pull the post-deploy bundle. Returns `true` iff a
 * reload was actually scheduled. Safe to call from an error burst — only the
 * first call in a tick, and only once per cooldown across reloads, acts.
 */
export function reloadForStaleBundleOnce(
  win: ReloadableWindow = window,
  now: number = Date.now(),
): boolean {
  if (reloadScheduled) return false;

  const last = readStamp(win);
  // No durable stamp store → we cannot guarantee loop-safety across the reload,
  // so we decline rather than risk a loop.
  if (last === 'unavailable') return false;
  if (!shouldReloadForStaleBundle(now, last)) return false;

  try {
    win.sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    return false;
  }
  reloadScheduled = true;
  win.location.reload();
  return true;
}

/** Test-only: clear the in-tick coalescing guard between cases. */
export function resetStaleBundleGuardForTests(): void {
  reloadScheduled = false;
}
